// @ts-nocheck
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SubSection, FieldLabel, inputClass } from "./SectionWrapper";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, GripVertical, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Props {
  event: any;
  updateEvent: (patch: Partial<any>) => void;
  eventId: string | undefined;
}

interface LevelRow {
  id?: string;
  label: string;
  description: string;
  sort_order: number;
  is_active: boolean;
  _new?: boolean;
}

const PRESETS: Record<string, { label: string; description?: string }[]> = {
  "AAU letters (A–F)": ["A", "B", "C", "D", "E", "F"].map((l) => ({ label: l })),
  "Lower / Medium / Higher": [
    { label: "Lower" },
    { label: "Medium" },
    { label: "Higher" },
  ],
  "Gold / Silver / Bronze": [
    { label: "Gold" },
    { label: "Silver" },
    { label: "Bronze" },
  ],
};

export function LevelsSection({ event, updateEvent, eventId }: Props) {
  const [rows, setRows] = useState<LevelRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [originalIds, setOriginalIds] = useState<string[]>([]);

  const enabled = !!event.registration_levels_enabled;
  const required = !!event.registration_levels_required;

  const load = async () => {
    if (!eventId) return;
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("event_registration_levels")
      .select("id, label, description, sort_order, is_active")
      .eq("event_id", eventId)
      .order("sort_order", { ascending: true });
    setLoading(false);
    if (error) {
      toast.error("Failed to load pools");
      return;
    }
    const list = (data || []).map((r: any) => ({
      id: r.id,
      label: r.label || "",
      description: r.description || "",
      sort_order: r.sort_order ?? 0,
      is_active: r.is_active !== false,
    }));
    setRows(list);
    setOriginalIds(list.map((r) => r.id).filter(Boolean));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const update = (i: number, patch: Partial<LevelRow>) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { label: "", description: "", sort_order: prev.length, is_active: true, _new: true },
    ]);
  };

  const removeRow = (i: number) => {
    if (!confirm("Remove this pool?")) return;
    setRows((prev) => prev.filter((_, idx) => idx !== i));
  };

  const move = (i: number, dir: -1 | 1) => {
    setRows((prev) => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next.map((r, idx) => ({ ...r, sort_order: idx }));
    });
  };

  const applyPreset = (key: string) => {
    if (!key || !PRESETS[key]) return;
    if (rows.length > 0) {
      if (!confirm("This will replace your current pools. Continue?")) return;
    }
    setRows(
      PRESETS[key].map((p, i) => ({
        label: p.label,
        description: p.description || "",
        sort_order: i,
        is_active: true,
        _new: true,
      }))
    );
  };

  const saveLevels = async () => {
    if (!eventId) {
      toast.error("Save the event first.");
      return;
    }
    const labels = rows.map((r) => r.label.trim()).filter(Boolean);
    if (new Set(labels).size !== labels.length) {
      toast.error("Pool labels must be unique.");
      return;
    }
    setSaving(true);
    try {
      // Delete removed
      const keptIds = rows.map((r) => r.id).filter(Boolean) as string[];
      const toDelete = originalIds.filter((id) => !keptIds.includes(id));
      if (toDelete.length) {
        await (supabase as any)
          .from("event_registration_levels")
          .delete()
          .in("id", toDelete);
      }
      // Upsert all current rows
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        if (!r.label.trim()) continue;
        const payload: any = {
          event_id: eventId,
          label: r.label.trim(),
          description: r.description?.trim() || null,
          sort_order: i,
          is_active: r.is_active,
          updated_at: new Date().toISOString(),
        };
        if (r.id) {
          await (supabase as any)
            .from("event_registration_levels")
            .update(payload)
            .eq("id", r.id);
        } else {
          await (supabase as any).from("event_registration_levels").insert(payload);
        }
      }
      toast.success("Pools saved");
      await load();
    } catch (e: any) {
      toast.error(e.message || "Failed to save pools");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) =>
            updateEvent({ registration_levels_enabled: e.target.checked })
          }
          className="mt-1"
        />
        <div>
          <div className="text-sm font-medium text-foreground">
            Enable pools / levels for this event
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            When enabled, coaches will pick their team's competitive level during
            registration. The scheduler will use these pools to seed bracket play.
          </p>
        </div>
      </label>

      {enabled && (
        <>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={required}
              onChange={(e) =>
                updateEvent({ registration_levels_required: e.target.checked })
              }
            />
            <span className="text-sm text-foreground">
              Make level selection required during registration
            </span>
          </label>

          <SubSection title="Help text shown to coaches (optional)">
            <textarea
              value={event.registration_levels_help_text || ""}
              onChange={(e) =>
                updateEvent({ registration_levels_help_text: e.target.value })
              }
              rows={4}
              placeholder="Explain your pool structure here. Coaches see this above the level dropdown during registration."
              className={inputClass}
            />
          </SubSection>

          <SubSection title="Pool labels">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Button type="button" size="sm" onClick={addRow}>
                <Plus className="w-4 h-4 mr-1" /> Add pool
              </Button>
              <select
                onChange={(e) => {
                  applyPreset(e.target.value);
                  e.currentTarget.value = "";
                }}
                defaultValue=""
                className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              >
                <option value="">Apply preset…</option>
                {Object.keys(PRESETS).map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
                <option value="">Custom (type your own)</option>
              </select>
              <div className="ml-auto">
                <Button
                  type="button"
                  size="sm"
                  onClick={saveLevels}
                  disabled={saving || !eventId}
                >
                  {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  Save pools
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
              </div>
            ) : rows.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
                No pools defined yet. Click "Add pool" or apply a preset.
              </div>
            ) : (
              <div className="space-y-2">
                {rows.map((row, i) => (
                  <div
                    key={row.id || `new-${i}`}
                    className="flex items-center gap-2 p-2 border border-border rounded-lg bg-card"
                  >
                    <div className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => move(i, -1)}
                        className="text-muted-foreground hover:text-foreground"
                        aria-label="Move up"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        onClick={() => move(i, 1)}
                        className="text-muted-foreground hover:text-foreground"
                        aria-label="Move down"
                      >
                        ▼
                      </button>
                    </div>
                    <GripVertical className="w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Label (e.g. A)"
                      value={row.label}
                      onChange={(e) => update(i, { label: e.target.value })}
                      className={`${inputClass} flex-shrink-0 w-32`}
                    />
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={row.description}
                      onChange={(e) => update(i, { description: e.target.value })}
                      className={`${inputClass} flex-1`}
                    />
                    <label className="flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={row.is_active}
                        onChange={(e) => update(i, { is_active: e.target.checked })}
                      />
                      Active
                    </label>
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      className="text-muted-foreground hover:text-red-600 p-1"
                      aria-label="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {!eventId && (
              <p className="text-xs text-amber-600 mt-2">
                Save the event basics first to persist pools.
              </p>
            )}
          </SubSection>
        </>
      )}
    </div>
  );
}
